/**
 * Hooks del dominio del Optimizador de Cortes (TanStack Query).
 *
 * Datos de servidor -> TanStack Query (`docs/frontend_conventions.md`, Datos): no
 * `useState`+`useEffect`+`fetch`. La red la gobierna `lib/api` (cookie httpOnly +
 * `X-Sede-Id`). El historial se cachea por sede + límite; al guardar un job se
 * **invalida** la raíz `['cutlist']` para que la lista refleje el nuevo registro.
 *
 * `useCutlistJobs` acepta `enabled`: el panel de historial solo dispara la query
 * cuando el usuario lo despliega (no se pide el historial al entrar a la pantalla).
 * `useCutlistJob` (recargar un job) se expone como mutación para invocarse a demanda
 * desde un botón, no como query automática.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { getActiveSedeId } from "@/lib/sede";

import {
  fetchCutlistJob,
  fetchCutlistJobs,
  saveCutlistJob,
  type SaveCutlistJobBody,
} from "../api";
import type { CutlistJob, CutlistJobResponse, CutlistJobsList } from "../types";
import { CUTLIST_ROOT_KEY, cutlistJobsQueryKey } from "./queryKeys";

/** Margen corto: evita refetch agresivo sin volver el historial rancio. */
const STALE_TIME = 30_000;

/**
 * Historial de optimizaciones de la sede activa. Solo se pide con `enabled` (el panel
 * de historial lo activa al desplegarse). `limit` por defecto 50 (lo acota el backend).
 */
export function useCutlistJobs(enabled: boolean, limit = 50) {
  const sedeId = getActiveSedeId();
  return useQuery<CutlistJobsList>({
    queryKey: cutlistJobsQueryKey(sedeId, limit),
    queryFn: () => fetchCutlistJobs(limit),
    enabled,
    staleTime: STALE_TIME,
  });
}

/**
 * Guardado de una optimización (`POST /cutlist/jobs`). Tras éxito invalida la raíz de
 * cutlist para que el historial muestre el nuevo job. El gating de permiso
 * (`use_cutlist`) lo decide la UI; aquí solo se ejecuta y se propaga el `ApiError`.
 */
export function useSaveCutlistJob() {
  const queryClient = useQueryClient();
  return useMutation<CutlistJobResponse, Error, SaveCutlistJobBody>({
    mutationFn: (body) => saveCutlistJob(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CUTLIST_ROOT_KEY });
    },
  });
}

/**
 * Recarga de un job del historial (`GET /cutlist/jobs/<id>`), como mutación para
 * invocarla a demanda (botón "Cargar" del historial). Devuelve el job completo para
 * que la pantalla restaure la entrada y el resultado.
 */
export function useReloadCutlistJob() {
  return useMutation<CutlistJob, Error, number>({
    mutationFn: async (jobId) => {
      const response = await fetchCutlistJob(jobId);
      return response.item;
    },
  });
}
